import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Archive, FileText, FolderKanban, Gift, Search, Plus } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ApiError } from '../../../lib/api'
import { useAuthSession } from '../../auth/session'
import {
  activateProgram,
  archiveProgram,
  createProgram,
  deleteProgramFromArchive,
  fetchExchangePacks,
  fetchProgramAssignments,
  fetchPrograms,
  pauseProgram,
  reactivateProgram,
  suspendProgram,
  syncProgramAssignments,
  updateProgram,
} from '../api'
import { fetchAgents } from '../../agents/api'
import { createProspect } from '../../prospects/api'
import { AddProspectMethodDialog } from '../../prospects/components/AddProspectMethodDialog'
import { ProgramFormDialog } from '../components/ProgramFormDialog'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  ProgramAssignmentDialog,
  ProgramCashRulesDialog,
  ProgramLifecycleConfirmDialog,
  ProgramRewardPackDialog,
  type ProgramLifecycleAction,
} from '../components/ProgramActionDialogs'
import { PageHeader, PageHeaderToolbar } from '@/components/app/PageHeader'
import { StateSurfaceIconAsset } from '@/components/app/StateSurface'
import { ProgramCard, ProgramCardSkeleton } from '../components/ProgramCard'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type {
  AssignedAgent,
  ProgramExchangeMode,
  ProgramMutationPayload,
  ProgramRecord,
  ProgramStatus,
} from '../../../types/programs'

const programQueryKey = ['programs', 'list']
const exchangePackQueryKey = ['exchange-packs', 'list']
const statusSortOrder: Record<ProgramStatus, number> = {
  active: 0,
  draft: 1,
  paused: 2,
  suspended: 3,
  archived: 4,
}

type ProgramExchangeModeFilter = 'all' | ProgramExchangeMode
type ProgramSortOption =
  | 'newest'
  | 'oldest'
  | 'status'
  | 'points-high'
  | 'points-low'
  | 'agents-high'
  | 'agents-low'

function isProgramExchangeModeFilter(value: string | null): value is ProgramExchangeModeFilter {
  return value === 'all' || value === 'cash' || value === 'reward' || value === 'both'
}

function isProgramSortOption(value: string | null): value is ProgramSortOption {
  return (
    value === 'newest' ||
    value === 'oldest' ||
    value === 'status' ||
    value === 'points-high' ||
    value === 'points-low' ||
    value === 'agents-high' ||
    value === 'agents-low'
  )
}

function programCreatedTime(program: ProgramRecord) {
  return new Date(program.created_at ?? program.updated_at ?? 0).getTime()
}

function programPointsValue(program: ProgramRecord) {
  return program.points_per_transaction ?? 0
}

function programAssignedAgentsValue(program: ProgramRecord) {
  return program.assigned_agents_count ?? program.assigned_agents?.length ?? 0
}

function programNameComparator(a: ProgramRecord, b: ProgramRecord) {
  return a.name.localeCompare(b.name)
}

function toProgramUpdatePayload(
  program: ProgramRecord,
  overrides: Partial<ProgramMutationPayload> = {},
): ProgramMutationPayload {
  return {
    name: program.name,
    description: program.description ?? '',
    commission_type: program.commission_type,
    exchange_mode: program.exchange_mode,
    points_per_transaction: program.points_per_transaction,
    points_per_euro: program.points_per_euro,
    exchange_pack_id: program.exchange_pack?.id ?? null,
    eligibility_criteria: program.eligibility_criteria ?? '',
    status: program.status,
    ...overrides,
  }
}

function ProgramsPageSkeleton() {
  return (
    <section className="app-section">
      <div className="flex flex-col gap-2 pb-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <Skeleton className="h-6 w-28" />
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <Skeleton className="h-8 w-full sm:w-[280px]" />
          <Skeleton className="h-8 w-full sm:w-[140px]" />
          <Skeleton className="h-8 w-full sm:w-[150px]" />
          <Skeleton className="h-8 w-full sm:w-[170px]" />
          <Skeleton className="h-8 w-full sm:w-[130px]" />
        </div>
      </div>

      <div className="app-grid md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <ProgramCardSkeleton key={index} />
        ))}
      </div>
    </section>
  )
}

export function ProgramsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, hasPermission } = useAuthSession()
  const search = searchParams.get('q') ?? ''
  const rawStatus = (searchParams.get('status') as 'all' | ProgramStatus | null) ?? 'all'
  const scopeFilter = searchParams.get('scope') === 'archived' ? 'archived' : 'programs'
  const businessFilterId = searchParams.get('businessId') ?? ''
  const rawModeFilter = searchParams.get('mode')
  const rawSort = searchParams.get('sort')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [noPacksDialogOpen, setNoPacksDialogOpen] = useState(false)
  const [editingProgram, setEditingProgram] = useState<ProgramRecord | null>(null)
  const [assignDialogProgram, setAssignDialogProgram] = useState<ProgramRecord | null>(null)
  const [cashDialogProgram, setCashDialogProgram] = useState<ProgramRecord | null>(null)
  const [rewardsDialogProgram, setRewardsDialogProgram] = useState<ProgramRecord | null>(null)
  const [pendingOwnerAction, setPendingOwnerAction] = useState<{
    type: ProgramLifecycleAction
    program: ProgramRecord
  } | null>(null)
  const [addProspectProgram, setAddProspectProgram] = useState<ProgramRecord | null>(null)
  const [createProspectError, setCreateProspectError] = useState<ApiError | null>(null)

  const programsQuery = useQuery({
    queryKey: programQueryKey,
    queryFn: fetchPrograms,
  })

  const packsQuery = useQuery({
    queryKey: exchangePackQueryKey,
    queryFn: () => fetchExchangePacks(),
    enabled: hasPermission('exchange-pack.view'),
  })

  const agentsQuery = useQuery({
    queryKey: ['agents', 'list'],
    queryFn: fetchAgents,
    enabled: hasPermission('agent.view'),
  })

  const createMutation = useMutation({
    mutationFn: createProgram,
    onSuccess: async () => {
      setDialogOpen(false)
      setEditingProgram(null)
      await queryClient.invalidateQueries({ queryKey: programQueryKey })
      await queryClient.refetchQueries({ queryKey: ['dashboard', 'business-summary'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({
      programId,
      payload,
    }: {
      programId: string
      payload: ProgramMutationPayload
    }) => updateProgram(programId, payload),
    onSuccess: async () => {
      setDialogOpen(false)
      setEditingProgram(null)
      await queryClient.invalidateQueries({ queryKey: programQueryKey })
    },
  })

  const activateMutation = useMutation({
    mutationFn: activateProgram,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: programQueryKey })
    },
  })

  const pauseMutation = useMutation({
    mutationFn: pauseProgram,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: programQueryKey })
    },
  })

  const reactivateMutation = useMutation({
    mutationFn: reactivateProgram,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: programQueryKey })
    },
  })

  const suspendMutation = useMutation({
    mutationFn: suspendProgram,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: programQueryKey })
    },
  })

  const archiveMutation = useMutation({
    mutationFn: archiveProgram,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: programQueryKey })
    },
  })

  const deleteArchivedMutation = useMutation({
    mutationFn: deleteProgramFromArchive,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: programQueryKey })
    },
  })

  const assignmentQuery = useQuery({
    queryKey: ['programs', 'assignments', assignDialogProgram?.id],
    queryFn: async () => {
      if (!assignDialogProgram) {
        return { data: [] as AssignedAgent[] }
      }
      return fetchProgramAssignments(assignDialogProgram.id)
    },
    enabled: Boolean(assignDialogProgram?.id),
  })

  const syncAssignmentsMutation = useMutation({
    mutationFn: ({ programId, agentIds }: { programId: string; agentIds: string[] }) =>
      syncProgramAssignments(programId, agentIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: programQueryKey })
      if (assignDialogProgram?.id) {
        await queryClient.invalidateQueries({
          queryKey: ['programs', 'assignments', assignDialogProgram.id],
        })
      }
      await queryClient.refetchQueries({ queryKey: ['dashboard', 'business-summary'] })
      setAssignDialogProgram(null)
    },
  })

  const updateRewardsPackMutation = useMutation({
    mutationFn: ({
      programId,
      payload,
    }: {
      programId: string
      payload: ProgramMutationPayload
    }) => updateProgram(programId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: programQueryKey })
      setRewardsDialogProgram(null)
    },
  })

  const createProspectMutation = useMutation({
    mutationFn: createProspect,
    onSuccess: async () => {
      setAddProspectProgram(null)
      setCreateProspectError(null)
      await queryClient.invalidateQueries({ queryKey: ['prospects', 'list'] })
    },
    onError: (err) => {
      setCreateProspectError(err as ApiError)
    },
  })

  const programs = useMemo(() => programsQuery.data?.data ?? [], [programsQuery.data])
  const packs = packsQuery.data?.data ?? []
  const rewardPacks = packs.filter((pack) => pack.status === 'active')
  const ownerCanCreate = hasPermission('program.create')
  const cardMode = user?.agent_profile !== null ? 'agent' : 'owner'
  const canSubmitProspect = hasPermission('prospect.submit')
  const mutationError = (createMutation.error ?? updateMutation.error) as ApiError | null
  const visiblePrograms = useMemo(
    () => (cardMode === 'agent' ? programs.filter((program) => program.status !== 'draft') : programs),
    [cardMode, programs],
  )
  const businessFilterOptions = useMemo(() => {
    const map = new Map<string, string>()
    for (const program of visiblePrograms) {
      if (!program.business_id) continue
      map.set(program.business_id, program.business_name ?? 'Business inconnu')
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [visiblePrograms])
  const showBusinessFilter = cardMode === 'agent' && businessFilterOptions.length > 1
  const effectiveBusinessFilterId = showBusinessFilter ? businessFilterId : ''
  const statusFilter: 'all' | ProgramStatus =
    cardMode === 'agent' && rawStatus === 'draft' ? 'all' : rawStatus
  const exchangeModeFilter: ProgramExchangeModeFilter = isProgramExchangeModeFilter(rawModeFilter)
    ? rawModeFilter
    : 'all'
  const ownerSortOptions: ProgramSortOption[] = [
    'newest',
    'oldest',
    'status',
    'points-high',
    'points-low',
    'agents-high',
    'agents-low',
  ]
  const agentSortOptions: ProgramSortOption[] = [
    'newest',
    'oldest',
    'status',
    'points-high',
    'points-low',
  ]
  const availableSortOptions = cardMode === 'owner' ? ownerSortOptions : agentSortOptions
  const sortOption: ProgramSortOption =
    isProgramSortOption(rawSort) && availableSortOptions.includes(rawSort) ? rawSort : 'newest'

  const filteredPrograms = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()

    return visiblePrograms
      .filter((program) => {
        const matchesScope =
          scopeFilter === 'archived' ? program.status === 'archived' : program.status !== 'archived'
        const matchesStatus =
          scopeFilter === 'archived' ? true : statusFilter === 'all' || program.status === statusFilter
        const matchesBusiness =
          effectiveBusinessFilterId.length === 0 || program.business_id === effectiveBusinessFilterId
        const matchesExchangeMode =
          exchangeModeFilter === 'all' || program.exchange_mode === exchangeModeFilter
        const matchesSearch =
          normalizedSearch.length === 0 ||
          program.name.toLowerCase().includes(normalizedSearch) ||
          (program.business_name ?? '').toLowerCase().includes(normalizedSearch) ||
          (program.description ?? '').toLowerCase().includes(normalizedSearch)

        return matchesScope && matchesStatus && matchesBusiness && matchesExchangeMode && matchesSearch
      })
      .sort((a, b) => {
        if (sortOption === 'oldest') {
          return programCreatedTime(a) - programCreatedTime(b) || programNameComparator(a, b)
        }
        if (sortOption === 'status') {
          return (
            statusSortOrder[a.status] - statusSortOrder[b.status] ||
            programCreatedTime(b) - programCreatedTime(a) ||
            programNameComparator(a, b)
          )
        }
        if (sortOption === 'points-high') {
          return (
            programPointsValue(b) - programPointsValue(a) ||
            programCreatedTime(b) - programCreatedTime(a) ||
            programNameComparator(a, b)
          )
        }
        if (sortOption === 'points-low') {
          return (
            programPointsValue(a) - programPointsValue(b) ||
            programCreatedTime(b) - programCreatedTime(a) ||
            programNameComparator(a, b)
          )
        }
        if (sortOption === 'agents-high') {
          return (
            programAssignedAgentsValue(b) - programAssignedAgentsValue(a) ||
            programCreatedTime(b) - programCreatedTime(a) ||
            programNameComparator(a, b)
          )
        }
        if (sortOption === 'agents-low') {
          return (
            programAssignedAgentsValue(a) - programAssignedAgentsValue(b) ||
            programCreatedTime(b) - programCreatedTime(a) ||
            programNameComparator(a, b)
          )
        }
        return programCreatedTime(b) - programCreatedTime(a) || programNameComparator(a, b)
      })
  }, [
    visiblePrograms,
    search,
    statusFilter,
    scopeFilter,
    effectiveBusinessFilterId,
    exchangeModeFilter,
    sortOption,
  ])

  const isOwnerActionPending =
    pauseMutation.isPending ||
    reactivateMutation.isPending ||
    suspendMutation.isPending ||
    archiveMutation.isPending ||
    deleteArchivedMutation.isPending

  if (programsQuery.isPending) {
    return <ProgramsPageSkeleton />
  }

  if (programsQuery.isError) {
    return (
      <article className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {(programsQuery.error as ApiError).message}
      </article>
    )
  }

  const isFilteringPrograms = search.trim() !== '' || statusFilter !== 'all' || exchangeModeFilter !== 'all'

  return (
    <section className={cn('app-section', filteredPrograms.length === 0 && 'flex flex-col min-h-[calc(100dvh-4rem)]')}>
      <PageHeader
        title={t('programs.title')}
        right={
          <PageHeaderToolbar>
          <Field className="w-full sm:min-w-[180px] sm:max-w-[360px] sm:flex-1">
            <FieldLabel htmlFor="programs-search" className="sr-only">
              {t('programs.searchPlaceholder')}
            </FieldLabel>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="programs-search"
                value={search}
                onChange={(event) => {
                  const nextValue = event.target.value
                  const nextParams = new URLSearchParams(searchParams)
                  if (nextValue.trim().length > 0) {
                    nextParams.set('q', nextValue)
                  } else {
                    nextParams.delete('q')
                  }
                  setSearchParams(nextParams, { replace: true })
                }}
                placeholder={t('programs.searchPlaceholder')}
                className="pl-9"
              />
            </div>
          </Field>

          <Select
            value={statusFilter}
            onValueChange={(value) => {
              const nextValue = value as 'all' | ProgramStatus
              const nextParams = new URLSearchParams(searchParams)
              if (nextValue === 'all') {
                nextParams.delete('status')
              } else {
                nextParams.set('status', nextValue)
              }
              setSearchParams(nextParams, { replace: true })
            }}
            disabled={scopeFilter === 'archived'}
          >
            <SelectTrigger size="sm" className="w-full sm:w-auto sm:min-w-[104px] sm:shrink-0">
              <SelectValue placeholder={t('common.status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>{t('common.status')}</SelectLabel>
                <SelectItem value="all">{t('programs.filters.allStatuses')}</SelectItem>
                <SelectItem value="active">{t('programs.status.active')}</SelectItem>
                {cardMode === 'owner' ? <SelectItem value="draft">{t('programs.status.draft')}</SelectItem> : null}
                <SelectItem value="paused">{t('programs.status.paused')}</SelectItem>
                <SelectItem value="suspended">{t('programs.status.suspended')}</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>

          {showBusinessFilter ? (
            <Select
              value={businessFilterId.length > 0 ? businessFilterId : 'all'}
              onValueChange={(value) => {
                const nextBusinessId = value === 'all' ? '' : value
                const nextParams = new URLSearchParams(searchParams)
                if (nextBusinessId.length > 0) {
                  nextParams.set('businessId', nextBusinessId)
                } else {
                  nextParams.delete('businessId')
                }
                setSearchParams(nextParams, { replace: true })
              }}
            >
              <SelectTrigger size="sm" className="w-full sm:w-auto sm:min-w-[104px] sm:shrink-0">
                <SelectValue placeholder={t('programs.filters.business')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>{t('programs.filters.business')}</SelectLabel>
                  <SelectItem value="all">{t('programs.filters.allBusinesses')}</SelectItem>
                  {businessFilterOptions.map((business) => (
                    <SelectItem key={business.id} value={business.id}>
                      {business.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          ) : null}

          <Select
            value={exchangeModeFilter}
            onValueChange={(value) => {
              const nextValue = value as ProgramExchangeModeFilter
              const nextParams = new URLSearchParams(searchParams)
              if (nextValue === 'all') {
                nextParams.delete('mode')
              } else {
                nextParams.set('mode', nextValue)
              }
              setSearchParams(nextParams, { replace: true })
            }}
          >
            <SelectTrigger size="sm" className="w-full sm:w-auto sm:min-w-[104px] sm:shrink-0">
              <SelectValue placeholder={t('programs.filters.exchangeMode')} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>{t('programs.filters.exchangeMode')}</SelectLabel>
                <SelectItem value="all">{t('programs.filters.allModes')}</SelectItem>
                <SelectItem value="cash">{t('programs.exchangeModes.cash')}</SelectItem>
                <SelectItem value="reward">{t('programs.exchangeModes.reward')}</SelectItem>
                <SelectItem value="both">{t('programs.exchangeModes.both')}</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>

          <Select
            value={sortOption}
            onValueChange={(value) => {
              const nextValue = value as ProgramSortOption
              const nextParams = new URLSearchParams(searchParams)
              if (nextValue === 'newest') {
                nextParams.delete('sort')
              } else {
                nextParams.set('sort', nextValue)
              }
              setSearchParams(nextParams, { replace: true })
            }}
          >
            <SelectTrigger size="sm" className="w-full sm:w-auto sm:min-w-[108px] sm:shrink-0">
              <SelectValue placeholder={t('programs.sortOptions.newest')} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>{t('programs.filters.sort')}</SelectLabel>
                <SelectItem value="newest">{t('programs.sortOptions.newest')}</SelectItem>
                <SelectItem value="oldest">{t('programs.sortOptions.oldest')}</SelectItem>
                <SelectItem value="status">{t('programs.sortOptions.status')}</SelectItem>
                <SelectItem value="points-high">{t('programs.sortOptions.pointsHigh')}</SelectItem>
                <SelectItem value="points-low">{t('programs.sortOptions.pointsLow')}</SelectItem>
                {cardMode === 'owner' ? (
                  <>
                    <SelectItem value="agents-high">{t('programs.sortOptions.agentsHigh')}</SelectItem>
                    <SelectItem value="agents-low">{t('programs.sortOptions.agentsLow')}</SelectItem>
                  </>
                ) : null}
              </SelectGroup>
            </SelectContent>
          </Select>

          {cardMode === 'owner' ? (
            <Button
              type="button"
              variant="secondary"
              className="gap-2 sm:shrink-0"
              onClick={() => navigate('/programs/docs')}
            >
              <FileText className="size-4" aria-hidden />
              {t('programs.documentation')}
            </Button>
          ) : null}

          {ownerCanCreate ? (
            <Button
              type="button"
              size="default"
              data-onboarding-target="program"
              onClick={() => {
                setEditingProgram(null)
                if (packs.length === 0) {
                  setNoPacksDialogOpen(true)
                } else {
                  setDialogOpen(true)
                }
              }}
              className="gap-2 sm:shrink-0"
            >
              <Plus className="size-4" aria-hidden />
              {t('programs.createProgram')}
            </Button>
          ) : null}
          <Tabs
            value={scopeFilter}
            onValueChange={(value) => {
              const nextScope = value === 'archived' ? 'archived' : 'programs'
              const nextParams = new URLSearchParams(searchParams)
              if (nextScope === 'archived') {
                nextParams.set('scope', 'archived')
              } else {
                nextParams.delete('scope')
              }
              setSearchParams(nextParams, { replace: true })
            }}
          >
            <TabsList>
              <TabsTrigger value="programs">
                <FolderKanban className="size-4" />
                {t('programs.tabs.programs')}
              </TabsTrigger>
              <TabsTrigger value="archived">
                <Archive className="size-4" />
                {t('programs.tabs.archived')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          </PageHeaderToolbar>
        }
      />

      {filteredPrograms.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center py-20 text-center">
          <StateSurfaceIconAsset icon={FolderKanban} size="md" />
          <h3 className="mt-6 text-xl font-semibold tracking-tight text-foreground">
            {t('programs.empty.title')}
          </h3>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground leading-relaxed">
            {isFilteringPrograms ? t('programs.noResults') : t('programs.empty.description')}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {isFilteringPrograms ? (
              <Button
                type="button"
                onClick={() => {
                  const next = new URLSearchParams()
                  if (scopeFilter === 'archived') next.set('scope', 'archived')
                  setSearchParams(next, { replace: true })
                }}
              >
                {t('common.clearFilters')}
              </Button>
            ) : ownerCanCreate ? (
              <Button
                type="button"
                onClick={() => {
                  if (packs.length === 0) {
                    setNoPacksDialogOpen(true)
                  } else {
                    setDialogOpen(true)
                  }
                }}
              >
                {t('programs.empty.cta')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="app-grid md:grid-cols-2 xl:grid-cols-3">
          {filteredPrograms.map((program) => (
            <ProgramCard
              key={program.id}
              program={program}
              mode={cardMode}
              canSubmitProspect={canSubmitProspect}
              onAddProspect={(p) => setAddProspectProgram(p)}
              togglePending={
                pauseMutation.isPending || reactivateMutation.isPending || suspendMutation.isPending
              }
              onEdit={(next) => {
                setEditingProgram(next)
                setDialogOpen(true)
              }}
              onTogglePause={(next) => {
                if (next.status === 'paused') {
                  setPendingOwnerAction({ type: 'reactivate', program: next })
                  return
                }
                setPendingOwnerAction({ type: 'pause', program: next })
              }}
              onEditCash={(next) => setCashDialogProgram(next)}
              onLiftSuspension={(next) => setPendingOwnerAction({ type: 'lift_suspension', program: next })}
              onActivateDraft={(next) => setPendingOwnerAction({ type: 'activate', program: next })}
              onSuspend={(next) => {
                setPendingOwnerAction({ type: 'suspend', program: next })
              }}
              onArchive={(next) => {
                setPendingOwnerAction({ type: 'archive', program: next })
              }}
              onDeleteArchived={(next) => {
                setPendingOwnerAction({ type: 'delete', program: next })
              }}
              onAssignAgents={(next) => setAssignDialogProgram(next)}
              onManageRewards={(next) => setRewardsDialogProgram(next)}
              businessPrograms={visiblePrograms.filter(
                (candidate) => candidate.business_id === program.business_id,
              )}
              onViewBusinessPrograms={(next) => {
                navigate(
                  `/programs?status=active&businessId=${encodeURIComponent(next.business_id)}`,
                )
              }}
              onEditRewardsPack={(next) => {
                if (next.exchange_pack?.id) {
                  navigate(`/exchange-packs?edit=${encodeURIComponent(next.exchange_pack.id)}`)
                  return
                }
                navigate('/exchange-packs')
              }}
            />
          ))}
        </div>
      )}

      <Dialog open={noPacksDialogOpen} onOpenChange={setNoPacksDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('programs.dialogs.noPacksTitle')}</DialogTitle>
            <DialogDescription>{t('programs.dialogs.noPacksDescription')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-4">
            <StateSurfaceIconAsset icon={Gift} size="md" />
            <p className="mt-4 text-sm text-muted-foreground text-center">
              {t('programs.dialogs.noPacksHint')}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setNoPacksDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={() => {
              setNoPacksDialogOpen(false)
              navigate('/exchange-packs')
            }}>
              {t('programs.dialogs.goToPacks')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ProgramFormDialog
        open={dialogOpen}
        title={editingProgram ? t('programs.dialogs.editTitle') : t('programs.dialogs.createTitle')}
        submitLabel={editingProgram ? t('programs.dialogs.save') : t('programs.dialogs.create')}
        packs={packs}
        initialProgram={editingProgram}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        error={mutationError}
        onClose={() => {
          setDialogOpen(false)
          setEditingProgram(null)
          createMutation.reset()
          updateMutation.reset()
        }}
        onSubmit={async (payload) => {
          try {
            if (editingProgram) {
              await updateMutation.mutateAsync({
                programId: editingProgram.id,
                payload,
              })
              return
            }

            await createMutation.mutateAsync(payload)
          } catch {
            // Mutation state already exposes the API error for inline rendering.
          }
        }}
      />

      <ProgramAssignmentDialog
        open={Boolean(assignDialogProgram)}
        program={assignDialogProgram}
        agents={agentsQuery.data?.data ?? []}
        assignments={assignmentQuery.data?.data ?? []}
        isSubmitting={syncAssignmentsMutation.isPending}
        error={(syncAssignmentsMutation.error as ApiError | null) ?? null}
        onClose={() => {
          setAssignDialogProgram(null)
          syncAssignmentsMutation.reset()
        }}
        onSubmit={async (agentIds) => {
          if (!assignDialogProgram) return
          await syncAssignmentsMutation.mutateAsync({
            programId: assignDialogProgram.id,
            agentIds,
          })
        }}
      />

      <ProgramCashRulesDialog
        open={Boolean(cashDialogProgram)}
        program={cashDialogProgram}
        isSubmitting={updateMutation.isPending}
        error={(updateMutation.error as ApiError | null) ?? null}
        onClose={() => {
          setCashDialogProgram(null)
          updateMutation.reset()
        }}
        onSubmit={async (pointsPerEuro) => {
          if (!cashDialogProgram) return
          await updateMutation.mutateAsync({
            programId: cashDialogProgram.id,
            payload: toProgramUpdatePayload(cashDialogProgram, { points_per_euro: pointsPerEuro }),
          })
          setCashDialogProgram(null)
        }}
      />

      <ProgramRewardPackDialog
        open={Boolean(rewardsDialogProgram)}
        program={rewardsDialogProgram}
        packs={rewardPacks}
        isSubmitting={updateRewardsPackMutation.isPending}
        error={(updateRewardsPackMutation.error as ApiError | null) ?? null}
        onClose={() => {
          setRewardsDialogProgram(null)
          updateRewardsPackMutation.reset()
        }}
        onSubmit={async (exchangePackId) => {
          if (!rewardsDialogProgram) return
          await updateRewardsPackMutation.mutateAsync({
            programId: rewardsDialogProgram.id,
            payload: toProgramUpdatePayload(rewardsDialogProgram, { exchange_pack_id: exchangePackId }),
          })
        }}
      />

      <ProgramLifecycleConfirmDialog
        action={pendingOwnerAction}
        isSubmitting={isOwnerActionPending}
        onClose={() => setPendingOwnerAction(null)}
        onConfirm={async (type, program) => {
          if (type === 'activate') {
            await activateMutation.mutateAsync(program.id)
          } else if (type === 'pause') {
            await pauseMutation.mutateAsync(program.id)
          } else if (type === 'reactivate' || type === 'lift_suspension') {
            await reactivateMutation.mutateAsync(program.id)
          } else if (type === 'suspend') {
            await suspendMutation.mutateAsync(program.id)
          } else if (type === 'archive') {
            await archiveMutation.mutateAsync(program.id)
          } else if (type === 'delete') {
            await deleteArchivedMutation.mutateAsync(program.id)
          }
          setPendingOwnerAction(null)
        }}
      />
      <AddProspectMethodDialog
        open={Boolean(addProspectProgram)}
        programId={addProspectProgram?.id ?? ''}
        agentCode={user?.agent_profile?.agent_code ?? ''}
        onClose={() => setAddProspectProgram(null)}
        isSubmittingForm={createProspectMutation.isPending}
        formError={createProspectError}
        onFormSubmit={async (payload) => {
          try { await createProspectMutation.mutateAsync(payload) } catch { /* error surfaced via formError */ }
        }}
      />

    </section>
  )
}
